/**
 * ============================================================================
 * SMART VENDOR MATCHING - AI-Powered Theme-to-Vendor Matching
 * ============================================================================
 * 
 * PURPOSE:
 * Automatically matches saved wedding themes with relevant vendors using AI.
 * Analyzes theme colors, styles, and preferences to recommend the best-fit
 * vendors across multiple categories (venues, photographers, caterers, etc.).
 * 
 * KEY FEATURES:
 * 1. Theme Selection - Choose from saved wedding themes
 * 2. Category Selection - Pick vendor categories to match (Venue, Photography, etc.)
 * 3. AI Matching Algorithm - Analyzes theme data to find best vendor matches
 * 4. Match Scoring - Shows compatibility percentage for each vendor
 * 5. Vendor Profiles - View detailed vendor information
 * 6. Shortlist Integration - Add matched vendors to shortlist
 * 
 * USER FLOW:
 * 1. Select a saved wedding theme from dropdown
 * 2. Choose vendor categories to match (default: Venue, Photography, Catering)
 * 3. Click "Find My Perfect Vendors"
 * 4. View AI-matched vendors with compatibility scores
 * 5. Click vendor cards to view profiles
 * 6. Add vendors to shortlist for comparison
 * 
 * MATCHING ALGORITHM:
 * - Analyzes theme colors and matches vendor color preferences
 * - Compares style tags (romantic, rustic, modern, etc.)
 * - Considers venue type, decor style, and aesthetic
 * - Calculates compatibility score (0-100%)
 * - Ranks vendors by match quality
 * 
 * BACKEND INTEGRATION:
 * - GET /api/themes - Fetch user's saved themes
 * - POST /api/vendors/smart-match - AI vendor matching
 * - GET /api/vendors/:id - Vendor profile details
 * 
 * STATE MANAGEMENT:
 * - selectedTheme: Currently selected wedding theme
 * - savedThemes: User's saved themes from dashboard
 * - selectedCategories: Vendor categories to match
 * - matchingResults: AI-matched vendors with scores
 * - selectedVendor: Vendor for profile modal
 * 
 * VENDOR CATEGORIES:
 * - Venue, Photography, Catering, Flowers, Music, Attire, Transportation
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import NotificationModal from '../../components/NotificationModal';
import VendorProfileModal from '../../components/VendorProfileModal';
import BackgroundIcons from '../../components/BackgroundIcons';
import AIMagicButton from '../../components/AIMagicButton';
import AIColorMagicLoader from '../../components/AIColorMagicLoader';
import VendorCard from '../../components/VendorCard';
import { formatVendorForModal } from '../../utils/vendorHelpers';

const SmartVendorMatching = () => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // ------------------------------------------------------------------------
  // THEME SELECTION STATE
  // ------------------------------------------------------------------------
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [vendorEntryPoint, setVendorEntryPoint] = useState('direct');
  // Currently selected wedding theme for vendor matching
  // null = no theme selected
  // object = theme data (colors, styles, mood, etc.)
  
  const [savedThemes, setSavedThemes] = useState([]);
  // User's saved wedding themes from dashboard
  // Loaded from backend on component mount
  // Array of theme objects: [{ id, theme_name, colors, styles, ... }, ...]
  
  // ------------------------------------------------------------------------
  // VENDOR CATEGORY SELECTION STATE
  // ------------------------------------------------------------------------
  const [selectedCategories, setSelectedCategories] = useState(['Venue', 'Photography', 'Catering']);
  // Vendor categories to match
  // Default: Venue, Photography, Catering (most common needs)
  // Options: Venue, Catering, Photography, Flowers, Music, Attire, Transportation, Planning
  
  // ------------------------------------------------------------------------
  // MATCHING RESULTS STATE
  // ------------------------------------------------------------------------
  const [matchingResults, setMatchingResults] = useState(null);
  // AI-matched vendors from backend
  // Structure: { 
  //   Venue: [{ vendor, compatibility_score, match_rationale }, ...],
  //   Photography: [...],
  //   Catering: [...]
  // }
  // null = no results yet
  
  // ------------------------------------------------------------------------
  // UI STATE
  // ------------------------------------------------------------------------
  const [loading, setLoading] = useState(false);
  // Loading state during vendor matching API call
  // true = showing loading animation
  // false = ready for interaction
  
  const [loadingThemes, setLoadingThemes] = useState(true);
  // Loading state for fetching saved themes
  // true = fetching themes from backend
  // false = themes loaded
  
  const [error, setError] = useState(null);
  // Error message display
  // null = no error
  // string = error message to show user
  
  const [isVisible, setIsVisible] = useState(false);
  // Page visibility animation state
  // Used for fade-in animation on mount
  
  // ------------------------------------------------------------------------
  // VENDOR PROFILE MODAL STATE
  // ------------------------------------------------------------------------
  const [selectedVendor, setSelectedVendor] = useState(null);
  // Vendor selected for profile modal
  // null = no vendor selected
  // object = vendor data for modal display
  
  const [showProfile, setShowProfile] = useState(false);
  // Vendor profile modal visibility
  // true = modal open
  // false = modal closed
  
  // ------------------------------------------------------------------------
  // NOTIFICATION STATE
  // ------------------------------------------------------------------------
  const [notification, setNotification] = useState({
    isOpen: false,      // Modal visibility
    type: 'error',      // Notification type: 'success', 'error', 'info'
    title: '',          // Notification title
    message: ''         // Notification message
  });
  
  // ------------------------------------------------------------------------
  // AI COLOR MAGIC MODAL STATE
  // ------------------------------------------------------------------------
  const [colorModal, setColorModal] = useState({ 
    show: false,                // Modal visibility
    color: '',                  // Selected color hex code
    position: 0,                // Position in color flow (0-100%)
    colorName: '',              // Human-readable color name
    applications: [],           // Wedding applications for the color
    selectedCategory: 'decor'   // Selected category: decor, flowers, attire, etc.
  });

  // ============================================================================
  // VENDOR CATEGORIES CONFIGURATION
  // ============================================================================
  // Available vendor categories with icons and color themes
  const categories = [
    { name: 'Venue', icon: '🏛️', color: 'from-rose-100 to-pink-200' },
    { name: 'Catering', icon: '🍽️', color: 'from-champagne-100 to-gold-200' },
    { name: 'Photography', icon: '📸', color: 'from-purple-100 to-lavender-200' },
    { name: 'Flowers', icon: '🌸', color: 'from-pink-100 to-rose-200' },
    { name: 'Music', icon: '🎵', color: 'from-blue-100 to-indigo-200' },
    { name: 'Attire', icon: '👗', color: 'from-mauve-100 to-purple-200' },
    { name: 'Transportation', icon: '🚗', color: 'from-teal-100 to-cyan-200' },
    { name: 'Planning', icon: '📋', color: 'from-blush-100 to-pink-200' }
  ];

  // ============================================================================
  // COLOR UTILITY FUNCTIONS
  // ============================================================================
  // TODO: Refactor - These helper functions are duplicated from Dream Wedding Generator
  // Should be moved to a shared utility file to maintain single source of truth
  
  /**
   * Get human-readable color name from hex code
   * Uses TheColorAPI with Color Pizza API fallback
   * 
   * @param {String} hex - Hex color code (e.g., '#FFB6C1')
   * @returns {String} Color name (e.g., 'Blush Pink')
   */
  const getColorName = async (hex) => {
    try {
      const cleanHex = hex.replace('#', '');
      const response = await fetch(`https://www.thecolorapi.com/id?hex=${cleanHex}`);
      const data = await response.json();
      return data.name.value;
    } catch (error) {
      // Fallback to Color Pizza API
      try {
        const cleanHex = hex.replace('#', '');
        const response = await fetch(`https://api.color.pizza/v1/${cleanHex}`);
        const data = await response.json();
        return data.colors[0].name;
      } catch (fallbackError) {
        return 'Custom Color';
      }
    }
  };

  /**
   * Get AI-generated wedding applications for a color
   * Calls backend smart suggestions API
   * 
   * @param {String} hex - Hex color code
   * @param {String} colorName - Human-readable color name
   * @param {String} selectedModel - AI model to use
   * @returns {Object|Array} Color applications or error indicator
   */
  const getSmartWeddingApplications = async (hex, colorName, selectedModel) => {
    try {
      const requestData = {
        color: hex,
        colorName: colorName,
        model: selectedModel || 'auto',
        timestamp: Date.now()
      };
      
      const response = await apiService.dream.generateSmartSuggestions(requestData);

      if (response.status === 200 && response.data) {
        try {
          let suggestions = response.data.suggestions || response.data;
          
          if (typeof suggestions === 'string') {
            const jsonMatch = suggestions.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              suggestions = JSON.parse(jsonMatch[0]);
            } else {
              return [suggestions];
            }
          }
          
          if (suggestions && typeof suggestions === 'object' && suggestions.decor) {
            return suggestions;
          } else if (Array.isArray(suggestions)) {
            return suggestions;
          } else {
            return [suggestions];
          }
        } catch (parseError) {
          if (Array.isArray(response.data)) {
            return response.data;
          } else if (typeof response.data === 'string') {
            return [response.data];
          } else {
            return ['API_ERROR'];
          }
        }
      } else {
        return ['API_ERROR'];
      }
    } catch (error) {
      return ['API_ERROR'];
    }
  };

  const triggerAIMagic = async () => {
    setColorModal(prev => ({ ...prev, applications: ['LOADING_AI_MAGIC'] }));
    
    let modelToUse = 'auto';
    if (selectedTheme && selectedTheme.ai_model) {
      modelToUse = selectedTheme.ai_model;
    }
    
    const smartApplications = await getSmartWeddingApplications(
      colorModal.color, 
      colorModal.colorName, 
      modelToUse
    );
    
    setTimeout(() => {
      if (Array.isArray(smartApplications) && smartApplications.includes('API_ERROR')) {
        setColorModal(prev => ({
          ...prev,
          applications: ['API_ERROR_MESSAGE']
        }));
      } else {
        setColorModal(prev => ({
          ...prev,
          applications: smartApplications
        }));
      }
    }, 2000);
  };

  // Category definitions for color modal
  const colorCategories = [
    { id: 'decor', name: 'Decor', icon: '🏺' },
    { id: 'flowers', name: 'Flowers', icon: '🌸' },
    { id: 'attire', name: 'Attire', icon: '👗' },
    { id: 'lighting', name: 'Lighting', icon: '💡' },
    { id: 'stationery', name: 'Stationery', icon: '📝' },
    { id: 'food', name: 'Food & Cake', icon: '🍰' }
  ];

  const getDisplayApplications = () => {
    if (!colorModal.applications) return [];
    
    if (Array.isArray(colorModal.applications) && colorModal.applications.includes('API_ERROR_MESSAGE')) {
      return ['API_ERROR_MESSAGE'];
    }
    
    if (typeof colorModal.applications === 'object' && !Array.isArray(colorModal.applications)) {
      return colorModal.applications[colorModal.selectedCategory] || [];
    }
    
    return Array.isArray(colorModal.applications) ? colorModal.applications : [];
  };

  // Helper function for RGB interpolation (copied from Theme Dashboard)
  const interpolateRgb = (color1, color2, factor) => {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);
    
    if (!rgb1 || !rgb2) return color1;
    
    const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * factor);
    const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * factor);
    const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * factor);
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // Parse array helper (copied from Theme Dashboard)
  const parseArray = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      return JSON.parse(field);
    } catch (error) {
      if (typeof field === 'string') {
        return field.split(',').map(item => item.trim()).filter(item => item.length > 0);
      }
      return [];
    }
  };

  // Parse colors helper (copied from Theme Dashboard)
  const parseColors = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      return JSON.parse(field);
    } catch (error) {
      if (typeof field === 'string') {
        return field.split(',').map(item => item.trim()).filter(item => item.length > 0);
      }
      return [];
    }
  };

  const extractHexFromColor = (colorString) => {
    const hexMatch = colorString.match(/#[A-Fa-f0-9]{6}/);
    return hexMatch ? hexMatch[0] : '#ccc';
  };

  const extractNameFromColor = (colorString) => {
    const parts = colorString.split(' - ');
    return parts.length > 1 ? parts[1] : 'Color';
  };

  const getContrastColor = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: h = 0;
      }
      h /= 6;
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  };

  useEffect(() => {
    setIsVisible(true);
    loadSavedThemes();
    
    // Check for URL params (from Dream Generator / Moodboard Matcher)
    const params = new URLSearchParams(window.location.search);
    const themeId = params.get('theme_id');
    const ritualIdsParam = params.get('ritual_ids');
    
    if (themeId && ritualIdsParam) {
      // Store ritual context for later use
      const ritualIds = JSON.parse(ritualIdsParam);
      sessionStorage.setItem('ritual_context', JSON.stringify({
        theme_id: parseInt(themeId),
        ritual_ids: ritualIds,
        from: params.get('from')
      }));
    }
  }, []);

  // Auto-select theme when coming from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const themeId = params.get('theme_id');
    
    if (themeId && savedThemes.length > 0 && !selectedTheme) {
      const theme = savedThemes.find(t => t.id === parseInt(themeId));
      if (theme) {
        setSelectedTheme(theme);
      }
    }
  }, [savedThemes, selectedTheme]);

  // Keyboard navigation for horizontal vendor strips
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle keyboard events when matching results are visible and not in modal
      if (!matchingResults || showProfile) return;
      
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        
        // Scroll all vendor strips horizontally
        const categories = Object.keys(matchingResults.matches);
        const scrollAmount = 300; // pixels to scroll
        
        categories.forEach(category => {
          const container = document.getElementById(`vendor-strip-${category}`);
          if (container) {
            const scrollDirection = e.key === 'ArrowLeft' ? -scrollAmount : scrollAmount;
            container.scrollBy({ left: scrollDirection, behavior: 'smooth' });
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [matchingResults, showProfile]);

  const loadSavedThemes = async () => {
    setLoadingThemes(true);
    try {
      const response = await apiService.savedThemes.getAll({ limit: 1000 });
      // console.log('API Response:', response);
      // console.log('Themes data:', response.data.themes);
      if (response.data.themes && response.data.themes.length > 0) {
        // console.log('First theme style_keywords:', response.data.themes[0].style_keywords);
        // console.log('Type of style_keywords:', typeof response.data.themes[0].style_keywords);
      }
      setSavedThemes(response.data.themes || []);
    } catch (error) {
      // console.error('Failed to load saved themes:', error);
      const token = localStorage.getItem('token');
      if (!token || error.response?.status === 401) {
        showNotification('info', 'Login Required', 'Please log in to use Smart Vendor Matching AI magic on your saved themes! ✨');
      } else {
        showNotification('error', 'Error', 'Failed to load your saved themes');
      }
    } finally {
      setLoadingThemes(false);
    }
  };

  const handleThemeSelect = (theme) => {
    setSelectedTheme(theme);
    setMatchingResults(null); // Clear previous results
  };

  const handleCategoryToggle = (categoryName) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryName)) {
        return prev.filter(cat => cat !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const findSmartMatches = async () => {
    if (!selectedTheme) {
      showNotification('error', 'Theme Required', 'Please select a theme to find matching vendors');
      return;
    }

    if (selectedCategories.length === 0) {
      showNotification('error', 'Categories Required', 'Please select at least one vendor category');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // console.log('🧠 Finding smart matches for theme:', selectedTheme.theme_name);
      // console.log('📋 Request payload:', {
      //   theme_id: selectedTheme.id,
      //   categories: selectedCategories
      //   // No limit - show all vendors in rankings
      // });
      
      const urlParams = new URLSearchParams(window.location.search);
      let vendorFrom = urlParams.get('from') || 'direct';
      if (vendorFrom !== 'direct') {
        const convKey = `vendor_conv_${selectedTheme.id}`;
        if (localStorage.getItem(convKey)) {
          vendorFrom = 'direct';
        } else {
          localStorage.setItem(convKey, '1');
        }
        urlParams.delete('from');
        const newUrl = urlParams.toString() ? `${window.location.pathname}?${urlParams}` : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
      setVendorEntryPoint(vendorFrom);

      const response = await fetch(`${API_BASE_URL}/vendors/match-with-theme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          theme_id: selectedTheme.id,
          categories: selectedCategories,
          from: vendorFrom
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      // console.log('🎯 API Response:', data);
      // console.log('📊 Matching stats:', data.matching_stats);
      // console.log('🏆 Matches by category:', data.matches);
      
      if (data.success) {
        setMatchingResults(data);
        showNotification('success', 'Smart Matching Complete!', 
          `Found ${data.matching_stats.total_vendors_analyzed} vendors analyzed with average compatibility of ${data.matching_stats.average_compatibility}%`);
      } else {
        throw new Error(data.error || 'Failed to find matching vendors');
      }

    } catch (error) {
      // console.error('Smart vendor matching failed:', error);
      setError(error.message);
      showNotification('error', 'Matching Failed', error.message);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message,
      ...(title === 'Login Required' ? { actionLabel: 'Log In', onAction: () => window.dispatchEvent(new Event('openAuthModal')) } : {})
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const handleVendorClick = (vendor) => {
    console.log('🔍 Raw vendor data:', vendor);
    
    const profileData = formatVendorForModal(vendor);
    console.log('🔍 Formatted profileData:', profileData);
    
    setSelectedVendor(profileData);
    setShowProfile(true);
  };

  const smartVendorMatchingIcons = [
    { emoji: '🌸', size: '4xl', opacity: '15' },
    { emoji: '🎯', size: '4xl', opacity: '15' },
    { emoji: '💖', size: '4xl', opacity: '15' },
    { emoji: '💍', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '4xl', opacity: '15' }
  ];

  return (
    <>
      {/* CSS Animation Styles */}
      <style jsx>{`
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      
      <div className="min-h-screen relative overflow-hidden">
      {/* Magical Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-pink-25 to-lavender-50"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-champagne-50/30 via-transparent to-blush-50/30"></div>
      
      {/* Background Icons */}
      <BackgroundIcons icons={smartVendorMatchingIcons} />
      
      {/* Floating Sparkles Animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10 hover:opacity-30 transition-opacity duration-300"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg animate-bounce" style={{ animationDelay: `${Math.random() * 2}s` }}>✨</span>
          </div>
        ))}
      </div>
      
      <div className={`container mx-auto px-4 py-8 relative z-10 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}>
        
        {/* Elegant Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <div className="relative">
              <span className="text-2xl">🔍</span>
              <span className="absolute -top-1 -right-1 text-lg">🏪</span>
            </div>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Smart Vendor Matching
          </h1>
          
          {/* Floral underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Discover perfect wedding vendors tailored to your style, budget, and vision
          </p>
        </div>

        {/* Magical Theme Selection */}
        <div className="relative mb-16">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-rose-50/80 rounded-3xl blur-sm"></div>
          <div className="relative bg-gradient-to-br from-white/90 to-rose-25/90 rounded-3xl shadow-2xl border border-white/50 backdrop-blur-sm p-8 md:p-12">
            
            {/* Section Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl shadow-xl mb-6 transform hover:scale-110 transition-all duration-300">
                <span className="text-3xl animate-bounce">🎨</span>
              </div>
              <h2 className="text-4xl font-serif font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent mb-4">
                Select Your Wedding Theme
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-rose-400 to-purple-400 mx-auto rounded-full shadow-lg"></div>
            </div>
            
            {loadingThemes ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mb-4"></div>
                <p className="text-lg text-gray-600">Loading your saved themes...</p>
              </div>
            ) : savedThemes.length === 0 ? (
              <div className="text-center py-16 relative">
                {/* Magical empty state */}
                <div className="absolute inset-0 bg-gradient-to-br from-rose-100/30 to-purple-100/30 rounded-2xl"></div>
                <div className="relative">
                  <div className="w-32 h-32 mx-auto mb-8 relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-rose-200 to-purple-200 rounded-full animate-pulse"></div>
                    <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                      <span className="text-6xl animate-bounce">🎨</span>
                    </div>
                  </div>
                  
                  <h3 className="text-3xl font-serif font-bold text-gray-800 mb-4">Create Your First Theme</h3>
                  <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                    Unlock the magic of AI-powered vendor matching by creating your dream wedding theme first!
                  </p>
                  
                  <div className="flex flex-col sm:flex-row justify-center gap-6">
                    <a href="/wedding-blueprint/dream-wedding-generator" className="group relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-rose-500 to-pink-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition-all duration-300"></div>
                      <div className="relative bg-gradient-to-r from-rose-500 to-pink-600 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-xl transform group-hover:scale-105 transition-all duration-300">
                        <span className="mr-2">✨</span>
                        Dream Generator
                      </div>
                    </a>
                    
                    <a href="/wedding-blueprint/moodboard-matcher" className="group relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition-all duration-300"></div>
                      <div className="relative bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-xl transform group-hover:scale-105 transition-all duration-300">
                        <span className="mr-2">🖼️</span>
                        Moodboard Matcher
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {savedThemes.map((theme, index) => (
                  <div
                    key={theme.id}
                    onClick={() => handleThemeSelect(theme)}
                    className={`group relative cursor-pointer transition-all duration-500 transform hover:scale-105 ${
                      selectedTheme?.id === theme.id ? 'scale-105' : ''
                    }`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Magical glow effect */}
                    <div className={`absolute inset-0 rounded-2xl transition-all duration-500 ${
                      selectedTheme?.id === theme.id
                        ? 'bg-gradient-to-br from-rose-400/30 to-purple-400/30 blur-xl scale-110'
                        : 'bg-gradient-to-br from-gray-200/20 to-gray-300/20 blur-lg opacity-0 group-hover:opacity-100'
                    }`}></div>
                    
                    {/* Theme card */}
                    <div className={`relative bg-gradient-to-br from-white to-rose-25 rounded-2xl border-2 transition-all duration-500 shadow-xl backdrop-blur-sm ${
                      selectedTheme?.id === theme.id
                        ? 'border-rose-400 shadow-2xl ring-4 ring-rose-200/50'
                        : 'border-white/50 hover:border-rose-300 hover:shadow-2xl'
                    }`}>
                      
                      {/* Selection indicator */}
                      {selectedTheme?.id === theme.id && (
                        <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                          <span className="text-white text-sm">✓</span>
                        </div>
                      )}
                      
                      <div className="p-6">
                        <h3 className="text-xl font-serif font-bold text-gray-800 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:to-purple-600 group-hover:bg-clip-text transition-all duration-300">
                          {theme.theme_name}
                        </h3>
                        <p className="text-gray-600 mb-6 line-clamp-2 leading-relaxed">{theme.description}</p>
                        
                        {/* 2D Color Orbs with Hover-Only Ping Animation - COMMENTED OUT FOR PERFORMANCE TESTING */}
                        {/* <div className="flex justify-center mb-6 relative">
                          <div className="flex gap-2">
                            {(theme.colors || []).slice(0, 5).map((color, colorIndex) => (
                              <div
                                key={colorIndex}
                                className="group/orb relative transform hover:scale-125 transition-all duration-300 cursor-pointer"
                                onClick={async () => {
                                  const selectedColor = extractHexFromColor(color);
                                  const colorName = await getColorName(selectedColor);
                                  
                                  setColorModal({
                                    show: true,
                                    color: selectedColor,
                                    position: 50,
                                    colorName: colorName,
                                    applications: ['SHOW_AI_BUTTON'],
                                    selectedCategory: 'decor'
                                  });
                                }}
                              >
                                <div 
                                  className="absolute inset-0 rounded-full animate-ping opacity-0 group-hover/orb:opacity-30 transition-opacity duration-300"
                                  style={{ 
                                    backgroundColor: extractHexFromColor(color),
                                    transform: 'scale(1.4)'
                                  }}
                                />
                                
                                <div
                                  className="w-10 h-10 rounded-full border-2 border-white shadow-lg hover:shadow-xl transition-all duration-300 relative z-10"
                                  style={{ 
                                    backgroundColor: extractHexFromColor(color)
                                  }}
                                />
                                
                                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap opacity-0 group-hover/orb:opacity-100 transition-all duration-300 z-20">
                                  <div 
                                    className="px-2 py-1 rounded-md shadow-lg border border-white font-medium text-xs capitalize"
                                    style={{ 
                                      backgroundColor: extractHexFromColor(color),
                                      color: getContrastColor(extractHexFromColor(color)),
                                      textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                                    }}
                                  >
                                    {color.split('(')[0].trim()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div> */}
                        
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Step 1: Check if user is logged in */}
        {!localStorage.getItem('token') ? (
          <div className="text-center py-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Please log in to use Smart Vendor Matching</h3>
          </div>
        ) : savedThemes.length === 0 ? (
          <div className="text-center py-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Create your first theme to get started</h3>
          </div>
        ) : 
          <>
        {/* Magical Category Selection */}
        <div className="relative mb-16">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-purple-50/80 rounded-3xl blur-sm"></div>
          <div className="relative bg-gradient-to-br from-white/90 to-purple-25/90 rounded-3xl shadow-2xl border border-white/50 backdrop-blur-sm p-8 md:p-12">
            
            {/* Section Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-2xl shadow-xl mb-6 transform hover:scale-110 transition-all duration-300">
                <span className="text-3xl animate-bounce">📋</span>
              </div>
              <h2 className="text-4xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent animate-pulse mb-4">
                Select Vendor Categories
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-indigo-400 mx-auto rounded-full shadow-lg"></div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {categories.map((category, index) => (
                <div
                  key={category.name}
                  onClick={() => handleCategoryToggle(category.name)}
                  className={`group relative cursor-pointer transition-all duration-500 transform hover:scale-105 ${
                    selectedCategories.includes(category.name) ? 'scale-105' : ''
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Magical glow effect */}
                  <div className={`absolute inset-0 rounded-2xl transition-all duration-500 ${
                    selectedCategories.includes(category.name)
                      ? 'bg-gradient-to-br from-rose-400/30 to-purple-400/30 blur-xl scale-110'
                      : 'bg-gradient-to-br from-gray-200/20 to-gray-300/20 blur-lg opacity-0 group-hover:opacity-100'
                  }`}></div>
                  
                  {/* Category card */}
                  <div className={`relative bg-gradient-to-br ${category.color} rounded-2xl border-2 transition-all duration-500 shadow-xl backdrop-blur-sm text-center p-6 ${
                    selectedCategories.includes(category.name)
                      ? 'border-rose-400 shadow-2xl ring-4 ring-rose-200/50'
                      : 'border-white/50 hover:border-rose-300 hover:shadow-2xl'
                  }`}>
                    
                    {/* Selection indicator */}
                    {selectedCategories.includes(category.name) && (
                      <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                        <span className="text-white text-sm">✓</span>
                      </div>
                    )}
                    
                    {/* Category icon with magical effects */}
                    <div className="relative mb-4">
                      <div className="w-16 h-16 mx-auto bg-white rounded-2xl shadow-lg flex items-center justify-center transform group-hover:rotate-12 transition-all duration-500">
                        <span className="text-4xl group-hover:scale-110 transition-all duration-300">{category.icon}</span>
                      </div>
                      
                      {/* Floating sparkles */}
                      <div className="absolute -top-2 -right-2 text-yellow-400 animate-pulse opacity-0 group-hover:opacity-100 transition-all duration-300">✨</div>
                      <div className="absolute -bottom-1 -left-1 text-pink-400 animate-pulse opacity-0 group-hover:opacity-100 transition-all duration-300 delay-200">💫</div>
                    </div>
                    
                    <h3 className="font-bold text-gray-800 text-lg group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:to-purple-600 group-hover:bg-clip-text transition-all duration-300">
                      {category.name}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Magical Find Matches Button */}
        <div className="text-center mb-16 relative">
          {/* Magical background effects */}
          <div className="absolute inset-0 flex justify-center items-center">
            <div className="w-64 h-32 bg-gradient-to-r from-rose-300/20 via-pink-300/20 to-purple-300/20 rounded-full blur-3xl animate-pulse"></div>
          </div>
          
          <div className="relative">
            <button
              onClick={findSmartMatches}
              disabled={loading || !selectedTheme || selectedCategories.length === 0}
              className="flex-1 relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-pulse"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                  ✨ Finding Perfect Matches...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-xl animate-bounce">🎯</span>
                  Find My Perfect Vendors
                </div>
              )}
              
              {/* Gentle pulse glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl animate-pulse"></div>
            </button>
            
            {/* Helper text */}
            {(!selectedTheme || selectedCategories.length === 0) && (
              <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 shadow-lg">
                <div className="flex items-center justify-center text-amber-700">
                  <span className="text-2xl mr-3 animate-bounce">💡</span>
                  <span className="font-medium">
                    {!selectedTheme ? 'Please select a wedding theme first' : 'Please select at least one vendor category'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Magical Matching Results */}
        {matchingResults && (
          <div className="relative bg-gradient-to-br from-white via-rose-50/30 to-pink-50/30 rounded-3xl shadow-2xl p-10 border border-rose-200/50 overflow-hidden">
            {/* Magical Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-10 w-32 h-32 bg-rose-300 rounded-full blur-3xl"></div>
              <div className="absolute top-20 right-20 w-24 h-24 bg-pink-300 rounded-full blur-2xl"></div>
              <div className="absolute bottom-10 left-1/3 w-28 h-28 bg-champagne-300 rounded-full blur-3xl"></div>
            </div>
            
            {/* Magical Header */}
            <div className="relative text-center mb-12">
              {/* Floating Sparkles */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                <div className="w-2 h-2 bg-rose-400 rounded-full animate-ping" style={{animationDelay: '0s'}}></div>
                <div className="w-2 h-2 bg-pink-400 rounded-full animate-ping" style={{animationDelay: '0.3s'}}></div>
                <div className="w-2 h-2 bg-champagne-400 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
              </div>
              
              <h2 className="text-5xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 leading-tight">
                ✨ AI Smart Matches for
              </h2>
              <div className="text-3xl font-semibold text-rose-700 mb-8 relative">
                "{matchingResults.theme_name}"
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full"></div>
              </div>
              
              {/* Glowy Eye-Catching Stats */}
              <div className="flex justify-center gap-8 flex-wrap">
                <div className="group relative bg-gradient-to-br from-rose-100 via-pink-100 to-rose-200 px-6 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-110 border border-rose-200 overflow-hidden">
                  {/* Glowing Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-rose-300/20 via-pink-300/30 to-rose-300/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  {/* Animated Border Glow */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-rose-400 via-pink-400 to-rose-400 opacity-0 group-hover:opacity-30 blur-sm transition-opacity duration-500 -z-10"></div>
                  <div className="relative">
                    <span className="block text-3xl font-bold text-rose-700 group-hover:text-rose-800 transition-colors duration-300">{matchingResults.matching_stats.total_vendors_analyzed}</span>
                    <span className="text-rose-600 text-sm font-medium group-hover:text-rose-700 transition-colors duration-300">✨ Vendors Analyzed</span>
                  </div>
                  {/* Floating Sparkles */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-1 h-1 bg-rose-400 rounded-full animate-ping"></div>
                  </div>
                </div>
                
                <div className="group relative bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 px-6 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-110 border border-blue-200 overflow-hidden">
                  {/* Glowing Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-300/20 via-indigo-300/30 to-blue-300/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  {/* Animated Border Glow */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-400 opacity-0 group-hover:opacity-30 blur-sm transition-opacity duration-500 -z-10"></div>
                  <div className="relative">
                    <span className="block text-3xl font-bold text-blue-700 group-hover:text-blue-800 transition-colors duration-300">{matchingResults.matching_stats.average_compatibility}%</span>
                    <span className="text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors duration-300">🎯 Average Compatibility</span>
                  </div>
                  {/* Floating Sparkles */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-1 h-1 bg-blue-400 rounded-full animate-ping"></div>
                  </div>
                </div>
                
                <div className="group relative bg-gradient-to-br from-emerald-100 via-green-100 to-emerald-200 px-6 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-110 border border-emerald-200 overflow-hidden">
                  {/* Glowing Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-300/20 via-green-300/30 to-emerald-300/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  {/* Animated Border Glow */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-400 via-green-400 to-emerald-400 opacity-0 group-hover:opacity-30 blur-sm transition-opacity duration-500 -z-10"></div>
                  <div className="relative">
                    <span className="block text-3xl font-bold text-emerald-700 group-hover:text-emerald-800 transition-colors duration-300">{matchingResults.matching_stats.highest_compatibility}%</span>
                    <span className="text-emerald-600 text-sm font-medium group-hover:text-emerald-700 transition-colors duration-300">🏆 Highest Match</span>
                  </div>
                  {/* Floating Sparkles */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-1 h-1 bg-emerald-400 rounded-full animate-ping"></div>
                  </div>
                </div>
              </div>
            </div>

            {Object.entries(matchingResults.matches).map(([category, vendors]) => (
              <div key={category} className="mb-12 relative">
                {/* Elegant Category Header */}
                <div className="relative mb-2 p-4 bg-gradient-to-r from-rose-50 via-pink-50 to-champagne-50 rounded-xl border border-rose-200/50 shadow-sm overflow-hidden group hover:shadow-md transition-all duration-300">
                  {/* Background Sparkles */}
                  <div className="absolute top-2 right-4 flex gap-1 opacity-60">
                    <div className="w-1 h-1 bg-rose-400 rounded-full animate-pulse" style={{animationDelay: '0s'}}></div>
                    <div className="w-1 h-1 bg-pink-400 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
                    <div className="w-1 h-1 bg-champagne-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-3xl transform group-hover:scale-110 transition-transform duration-300 animate-bounce">
                        {categories.find(cat => cat.name === category)?.icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:via-pink-600 group-hover:to-purple-600 transition-all duration-300">
                          {category}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="bg-gradient-to-r from-rose-100 via-pink-100 to-rose-200 px-4 py-2 rounded-full border border-rose-200 shadow-sm">
                        <span className="text-rose-700 font-bold">{vendors.length}</span>
                        <span className="text-rose-600 text-sm ml-1">matches</span>
                      </div>
                      {vendors.length > 0 && (
                        <div className="bg-gradient-to-r from-emerald-100 to-green-100 px-3 py-2 rounded-full border border-emerald-200 shadow-sm">
                          <span className="text-emerald-700 font-bold text-sm">
                            🏆 {Math.max(...vendors.map(v => v.compatibility_score))}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Magical Underline */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-300 via-pink-300 to-rose-300 opacity-50"></div>
                </div>

                {/* Subtle Scroll Hint */}
                <div className="text-center mb-2">
                  <p className="text-gray-400 text-sm animate-pulse">Scroll to explore →</p>
                </div>
                
                {vendors.length === 0 ? (
                  <div className="text-center py-16 bg-gradient-to-br from-rose-50 via-pink-50 to-champagne-50 rounded-2xl border border-rose-200 shadow-lg">
                    <div className="text-6xl mb-6 animate-bounce">🔍</div>
                    <p className="text-rose-600 text-xl font-semibold mb-2">No magical matches found</p>
                    <p className="text-rose-400 text-sm">Try adjusting your theme or selecting different categories</p>
                    <div className="mt-4 flex justify-center">
                      <div className="w-16 h-1 bg-gradient-to-r from-rose-300 to-pink-300 rounded-full"></div>
                    </div>
                  </div>
                ) : (
                  <div className="vendor-photo-roll-container relative">
                    {/* Magical Horizontal Photo Roll Strip */}
                    <div className="relative">
                      {/* Elegant Arc Navigation - Left */}
                      <button
                        onClick={() => {
                          const container = document.getElementById(`vendor-strip-${category}`);
                          container.scrollBy({ left: -350, behavior: 'smooth' });
                        }}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-20 w-8 h-16 bg-gradient-to-r from-rose-200/30 to-pink-200/30 hover:from-rose-300/60 hover:to-pink-300/60 rounded-r-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-end pr-1 group border-r border-rose-300/40"
                        title="Scroll left"
                      >
                        <span className="text-rose-500 group-hover:text-rose-700 text-lg font-bold">‹</span>
                      </button>
                      
                      {/* Elegant Arc Navigation - Right */}
                      <button
                        onClick={() => {
                          const container = document.getElementById(`vendor-strip-${category}`);
                          container.scrollBy({ left: 350, behavior: 'smooth' });
                        }}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-20 w-8 h-16 bg-gradient-to-r from-rose-200/30 to-pink-200/30 hover:from-rose-300/60 hover:to-pink-300/60 rounded-l-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-start pl-1 group border-l border-rose-300/40"
                        title="Scroll right"
                      >
                        <span className="text-rose-500 group-hover:text-rose-700 text-lg font-bold">›</span>
                      </button>
                      
                      {/* Gradient Fade Edges */}
                      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
                      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
                      
                      <div 
                        id={`vendor-strip-${category}`}
                        className="flex gap-6 overflow-x-auto pb-6 scroll-smooth vendor-magic-scroll"
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none'
                        }}
                      >
                        {vendors.map((vendor, index) => (
                          <VendorCard
                            key={vendor.id}
                            vendor={vendor}
                            index={index}
                            category={category}
                            onVendorClick={handleVendorClick}
                            showCompatibility={true}
                            layoutMode="horizontal"
                          />
                        ))}
                      </div>
                    </div>

                    {/* Magical Custom Scrollbar Styles */}
                    <style jsx>{`
                      .vendor-magic-scroll::-webkit-scrollbar {
                        height: 12px;
                      }
                      .vendor-magic-scroll::-webkit-scrollbar-track {
                        background: linear-gradient(to right, #fdf2f8, #fce7f3, #fdf2f8);
                        border-radius: 6px;
                        border: 1px solid #fce7f3;
                      }
                      .vendor-magic-scroll::-webkit-scrollbar-thumb {
                        background: linear-gradient(to right, #f472b6, #ec4899, #be185d);
                        border-radius: 6px;
                        border: 2px solid #fdf2f8;
                        box-shadow: 0 2px 4px rgba(244, 114, 182, 0.3);
                      }
                      .vendor-magic-scroll::-webkit-scrollbar-thumb:hover {
                        background: linear-gradient(to right, #ec4899, #db2777, #9d174d);
                        box-shadow: 0 4px 8px rgba(244, 114, 182, 0.4);
                      }
                      .vendor-magic-scroll::-webkit-scrollbar-corner {
                        background: #fdf2f8;
                      }
                    `}</style>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="flex items-center">
              <span className="text-red-500 text-xl mr-3">⚠️</span>
              <div>
                <h3 className="font-semibold text-red-800">Matching Error</h3>
                <p className="text-red-600">{error}</p>
              </div>
            </div>
          </div>
        )}
          </>
        }
      </div>

      {/* Vendor Profile Modal */}
      {showProfile && selectedVendor && (
        <VendorProfileModal
          isOpen={showProfile}
          vendor={selectedVendor}
          onClose={() => setShowProfile(false)}
          source="matching"
          themeId={selectedTheme?.id}
          entryPoint={vendorEntryPoint}
          onShowNotification={showNotification}
        />
      )}

      {/* Modals */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        title={notification.title}
        message={notification.message}
      />

      {showProfile && selectedVendor && (
        <VendorProfileModal
          vendor={selectedVendor}
          onClose={() => setShowProfile(false)}
          source="smart-matching"
          entryPoint={vendorEntryPoint}
        />
      )}

      {/* Color Information Modal - Same as Dream Wedding Generator */}
      {colorModal.show && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-lg flex items-center justify-center z-60 p-4 animate-in fade-in duration-500">
          <div className="bg-gradient-to-br from-white via-rose-50/30 to-champagne-50/30 backdrop-blur-xl rounded-3xl shadow-2xl max-w-sm sm:max-w-md lg:max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto transform animate-in zoom-in slide-in-from-bottom-4 duration-700 border border-white/20">
            {/* Modal Header with Color Preview */}
            <div className="relative overflow-hidden">
              <div 
                className="h-32 sm:h-40 relative flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${colorModal.color}E6, ${colorModal.color}B3, ${colorModal.color}80)`
                }}
              >
                {/* Animated background pattern */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-black/20"></div>
                  <div className="absolute top-4 left-4 w-24 sm:w-32 h-24 sm:h-32 bg-white/10 rounded-full animate-pulse"></div>
                  <div className="absolute bottom-4 right-4 w-16 sm:w-24 h-16 sm:h-24 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>
                
                <div className="relative z-10 text-center">
                  {/* Color circle with bounce animation */}
                  <div className="relative mb-4">
                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full border-4 border-white shadow-2xl mx-auto animate-bounce" style={{ backgroundColor: colorModal.color }}></div>
                    <div className="absolute inset-0 w-16 sm:w-20 h-16 sm:h-20 rounded-full border-2 border-white/50 mx-auto animate-ping"></div>
                  </div>
                  
                  {/* Typography */}
                  <h3 className="text-white text-xl sm:text-2xl font-serif font-bold drop-shadow-2xl mb-1 tracking-wide">
                    {colorModal.colorName}
                  </h3>
                  <p className="text-white/90 text-xs sm:text-sm font-light tracking-wider drop-shadow-lg">
                    Discovered in your theme palette
                  </p>
                </div>
                
                {/* Close button */}
                <button
                  onClick={() => setColorModal({ show: false, color: '', position: 0, colorName: '', applications: [], selectedCategory: 'decor' })}
                  className="absolute top-4 right-4 w-8 sm:w-10 h-8 sm:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm border border-white/20"
                >
                  <span className="text-sm sm:text-lg">✕</span>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="space-y-4 sm:space-y-6">
                {/* Color Codes */}
                <div className="bg-gradient-to-r from-gray-50/80 via-white/80 to-gray-50/80 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-gray-200/50 shadow-lg">
                  <h4 className="font-serif font-bold text-gray-800 mb-4 flex items-center text-base sm:text-lg">
                    <span className="mr-3 text-xl sm:text-2xl animate-bounce">🎨</span>
                    Color Specifications
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-between items-center p-2 sm:p-3 bg-white/60 rounded-xl border border-gray-100">
                      <span className="text-gray-600 font-medium text-sm sm:text-base">HEX Code:</span>
                      <code className="bg-gradient-to-r from-gray-100 to-gray-200 px-2 sm:px-3 py-1 sm:py-2 rounded-lg font-mono text-xs sm:text-sm font-bold text-gray-800 shadow-sm">
                        {colorModal.color}
                      </code>
                    </div>
                    {(() => {
                      const rgb = hexToRgb(colorModal.color);
                      const hsl = rgb ? rgbToHsl(rgb.r, rgb.g, rgb.b) : null;
                      return (
                        <>
                          {rgb && (
                            <div className="flex justify-between items-center p-2 sm:p-3 bg-white/60 rounded-xl border border-gray-100">
                              <span className="text-gray-600 font-medium text-sm sm:text-base">RGB Values:</span>
                              <code className="bg-gradient-to-r from-gray-100 to-gray-200 px-2 sm:px-3 py-1 sm:py-2 rounded-lg font-mono text-xs sm:text-sm font-bold text-gray-800 shadow-sm">
                                {rgb.r}, {rgb.g}, {rgb.b}
                              </code>
                            </div>
                          )}
                          {hsl && (
                            <div className="flex justify-between items-center p-2 sm:p-3 bg-white/60 rounded-xl border border-gray-100">
                              <span className="text-gray-600 font-medium text-sm sm:text-base">HSL Values:</span>
                              <code className="bg-gradient-to-r from-gray-100 to-gray-200 px-2 sm:px-3 py-1 sm:py-2 rounded-lg font-mono text-xs sm:text-sm font-bold text-gray-800 shadow-sm">
                                {hsl.h}°, {hsl.s}%, {hsl.l}%
                              </code>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Wedding Applications */}
                <div className="bg-gradient-to-r from-rose-50/80 via-pink-50/80 to-purple-50/80 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-rose-100/50 shadow-lg">
                  <h4 className="font-serif font-bold text-gray-800 mb-4 flex items-center text-base sm:text-lg">
                    <span className="mr-3 text-xl sm:text-2xl animate-bounce">💡</span>
                    AI Color Magic
                    {getDisplayApplications()[0] === 'LOADING_AI_MAGIC' && (
                      <span className="ml-2 text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-1 rounded-full animate-pulse">AI Powered</span>
                    )}
                  </h4>
                  
                  {/* Category Tabs - Only show after AI Magic is triggered */}
                  {getDisplayApplications()[0] !== 'SHOW_AI_BUTTON' && (
                    <div className="flex flex-wrap gap-2 mb-4">
                    {colorCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setColorModal(prev => ({ ...prev, selectedCategory: category.id }))}
                        className={`px-3 py-2 rounded-full text-xs font-semibold transition-all duration-300 flex items-center gap-1 ${
                          colorModal.selectedCategory === category.id
                            ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg transform scale-105'
                            : 'bg-white/70 text-gray-600 hover:bg-gradient-to-r hover:from-rose-100 hover:to-pink-100 hover:text-rose-600 hover:shadow-md'
                        }`}
                      >
                        <span>{category.icon}</span>
                        <span>{category.name}</span>
                      </button>
                    ))}
                    </div>
                  )}
                  
                  <div className="text-gray-700 space-y-2 font-light text-sm sm:text-base">
                    {getDisplayApplications()[0] === 'SHOW_AI_BUTTON' ? (
                      <AIMagicButton 
                        colorName={colorModal.colorName} 
                        onTriggerMagic={triggerAIMagic}
                      />
                    ) : getDisplayApplications()[0] === 'LOADING_AI_MAGIC' ? (
                      <AIColorMagicLoader colorName={colorModal.colorName} />
                    ) : getDisplayApplications()[0] === 'API_ERROR_MESSAGE' ? (
                      <div className="text-center p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                        <div className="text-4xl mb-3">⚠️</div>
                        <h5 className="font-bold text-red-700 mb-2">AI Service Temporarily Unavailable</h5>
                        <p className="text-red-600 text-sm mb-4">
                          Our AI color magic service is experiencing issues. Please try again in a few moments.
                        </p>
                        <button
                          onClick={triggerAIMagic}
                          className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg font-semibold hover:from-red-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105"
                        >
                          Try Again
                        </button>
                      </div>
                    ) : (
                      getDisplayApplications().map((application, index) => (
                        <p key={index} className="flex items-start">
                          <span className="mr-2 text-rose-400 mt-1">•</span> 
                          <span>
                            {application}
                          </span>
                        </p>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default SmartVendorMatching;
// Trigger deployment
